import { useEffect, useMemo, useState } from 'react';
import { ApiError, getPoolOffer, type ApiClient, type GameKind, type PoolOfferDetail } from '../api';
import { cacheGameKind, readCachedGameKind } from './gameKindCache';

export interface GameKindState {
  /** Null until the first answer. */
  kind: GameKind | null;
  /** The launched game's offer once fetched; null when the kind came from the cache or the fetch failed. */
  offer: PoolOfferDetail | null;
  loading: boolean;
}

interface Answer {
  gameId: string;
  kind: GameKind;
  offer: PoolOfferDetail | null;
}

const LOOT_PACK: GameKindState = { kind: 'loot_pack', offer: null, loading: false };

// A 401 means the stored token was dead. The app's expiry handler drops it before the request rejects, so the
// second request goes out as the anonymous visitor the player has become (an embed's public twin).
async function fetchOffer(client: ApiClient, gameId: string): Promise<PoolOfferDetail> {
  try {
    return await getPoolOffer(client, gameId);
  } catch (err) {
    // The client has dropped its in-flight GET entry by now, so the retry goes out without the dropped token.
    if (err instanceof ApiError && err.status === 401) return getPoolOffer(client, gameId);
    throw err;
  }
}

/**
 * Which kind of game the launch `game_id` names. A failed lookup answers `loot_pack`, as before custom packs
 * existed, unless the tab already learned the game is a custom pack: that one never falls back to a loot-pack page.
 */
export function useGameKind(client: ApiClient, gameId: string | null): GameKindState {
  const cachedKind = useMemo(() => (gameId === null ? null : readCachedGameKind(gameId)), [gameId]);
  const [answer, setAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    // A cached loot pack is a whole answer; a cached custom pack still needs its offer.
    if (gameId === null || cachedKind === 'loot_pack') return;
    let cancelled = false;
    void fetchOffer(client, gameId).then(
      (offer) => {
        if (cancelled) return;
        cacheGameKind(gameId, offer.gameKind);
        setAnswer({ gameId, kind: offer.gameKind, offer });
      },
      () => {
        if (!cancelled) setAnswer({ gameId, kind: cachedKind ?? 'loot_pack', offer: null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [client, gameId, cachedKind]);

  if (gameId === null) return LOOT_PACK;
  if (answer?.gameId === gameId) return { kind: answer.kind, offer: answer.offer, loading: false };
  if (cachedKind === 'loot_pack') return LOOT_PACK;
  return { kind: cachedKind, offer: null, loading: true };
}

/**
 * The launched game's kind, as the boot routes on it. Only a game this tab already knows to be a custom pack is
 * looked up before anything renders, since the lab needs its offer. Every other launch, every loot pack's included,
 * is answered at once with no request: the pack page's own offer request sends a custom pack on to the lab, and that
 * hand-off stores the kind for the load it starts. A preview asking for a lab state (`anyKind`) looks any game up.
 *
 * Decided once per game id: a kind the page stores on its way out must not start a lookup on the page it leaves.
 */
export function useLaunchGameKind(client: ApiClient, gameId: string | null, anyKind: boolean): GameKindState {
  const lookUp = useMemo(
    () => gameId !== null && (anyKind || readCachedGameKind(gameId) === 'custom_pack'),
    [gameId, anyKind],
  );
  return useGameKind(client, lookUp ? gameId : null);
}
